import { bookCoverFile, type BookSummary } from "./book-summary";
import { characterImagePublicUrl, matchingImageFile } from "./character-image";
import { portraitIndexUrl, portraitUrls, type PortraitIndex } from "./portrait-index";

export interface BookHome {
  summary: BookSummary;
  coverUrl: string | undefined;
  /** People with a portrait who are not marked minor. */
  cast: { id: string; displayName: string; scenes: number; imageUrl: string | undefined }[];
}

async function readIndex(bookId: string, base: string | undefined): Promise<PortraitIndex> {
  try {
    const response = await fetch(portraitIndexUrl(bookId, base));
    if (response.ok && (response.headers.get("content-type") ?? "").includes("json")) {
      return await response.json() as PortraitIndex;
    }
  } catch {
    // The page still works without portraits.
  }
  return { files: [] };
}

/** A character's portrait address in a book, if they have one. */
export async function loadPortraitUrls(
  bookId: string,
): Promise<(characterId: string) => string | undefined> {
  const base = import.meta.env.VITE_PORTRAIT_BASE;
  const index = await readIndex(bookId, base);
  const urls = portraitUrls(index, base);
  return (characterId) => {
    const file = matchingImageFile(index.files, characterId);
    return file === undefined ? undefined : urls?.[file] ?? characterImagePublicUrl(bookId, file);
  };
}

/** Who is marked minor, which admin can change at any time. */
export async function loadMinorIds(bookId: string): Promise<ReadonlySet<string>> {
  const index = await readIndex(bookId, import.meta.env.VITE_PORTRAIT_BASE);
  return new Set(index.minorIds ?? []);
}

export async function loadBookHome(bookId: string): Promise<BookHome> {
  const response = await fetch(`/book-data/${bookId}/book.json`);
  if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) {
    throw new Error(`No summary for ${bookId}`);
  }
  const summary = await response.json() as BookSummary;
  const base = import.meta.env.VITE_PORTRAIT_BASE;
  const index = await readIndex(bookId, base);
  const urls = portraitUrls(index, base);
  const urlOf = (file: string | undefined) => (file === undefined
    ? undefined
    : urls?.[file] ?? characterImagePublicUrl(bookId, file));
  const minor = new Set(index.minorIds ?? []);
  return {
    summary,
    coverUrl: urlOf(bookCoverFile(index.files)),
    cast: summary.topCast
      .filter((person) => !minor.has(person.id))
      .map((person) => ({
        ...person,
        imageUrl: urlOf(matchingImageFile(index.files, person.id)),
      })),
  };
}
